import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import i18n from '../../i18n';
import Conversation from '../../models/Conversation';
import Exchange from '../../models/Exchange';
import User from '../../models/User';
import { fetchAvailabilities } from '../../actions/availabilities';
import Api from '../../api/Api';

import { TransportationBanner } from './TransportationBanner';
import ButtonAddToCalendar from './ButtonAddToCalendar';
import ExchangeTabs from './ExchangeTabs';

import useGTM from '../analytics/useGTM';
import { NAVIGATION_ACTION, NAVIGATION_AREAS, NAVIGATION_TEXT } from '../analytics/analytics-constants';
import Analytics from '../../utils/analytics';
import { Button, MapGoogle, MapPin } from '@homeexchange/design';
import { ExchangeInfosButtons } from './ExchangeInfosButtons';

class ExchangeInfos extends React.Component {
    static propTypes = {
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        user: PropTypes.instanceOf(User).isRequired,
        fetchAvailabilities: PropTypes.func
    };

    constructor(props, context) {
        super(props, context);
        this.print = this.print.bind(this);
        this.back = this.back.bind(this);
        this.sendContactEmail = this.sendContactEmail.bind(this);
        this.shouldDisplayInitialExchangeDates = this.shouldDisplayInitialExchangeDates.bind(this);
        this.infosRef = React.createRef();
    }

    componentDidMount() {
        this.props.fetchAvailabilities(this.props.conversation.getExchange().get('home').id);

        const exchange = this.props.conversation.getExchange();
        if (!exchange.get('update_status') && !this.props.conversation.isFinalized()) {
            document.dispatchEvent(new CustomEvent('dismiss_card'));
        }
    }

    print(e) {
        e.preventDefault();
        const printWindow = window.open(
            '',
            'mywindow',
            `status=1,width=767,height=${document.body.clientHeight}`
        );
        printWindow.document.write('<html>');
        printWindow.document.write(document.head.outerHTML);
        printWindow.document.write('<body onafterprint="self.close()">');
        printWindow.document.write(`<div class="print">${this.infosRef.current.outerHTML}</div>`);
        printWindow.document.write(`<script>window.print();</script>`);
        printWindow.document.write('</body></html>');
    }

    back(e) {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('dismiss_card', { detail: { animate: false } }));
    }

    sendContactEmail(reasonLabel, reasonMsg) {
        const { conversation, user } = this.props;
        const isAsHostExchange = Boolean(conversation.getExchangeAsHost());
        const exchangeTypeLabel = conversation.isMutual() ? 'reciprocal' : 'GP';
        const cancellerLabel = isAsHostExchange ? 'host' : 'guest';
        const conversationId = conversation.get('id');

        const data = {
            email: user.get('email'),
            locale: i18n.language,
            subject: `Cancellation by ${cancellerLabel} for conversation ${conversationId} and ${exchangeTypeLabel} exchange`,
            message: `
                        ${reasonLabel}

                        ${reasonMsg}
                    `,
            status: 'new',
            priority: 'urgent',
            tags: ['cancellation'],
            extra_informations: [`user ${user.id}`, `conversation ${conversationId}`]
        };

        Api.Utils.contact(data);
    }

    shouldDisplayInitialExchangeDates() {
        const exchange = this.props.conversation.getExchange();

        return (
            exchange.get('update_status') &&
            exchange.get('update_status') === Exchange.UPDATE_STATUS_NEED_BILLING
        );
    }

    getExchangeToDisplay(conversation) {
        if (
            conversation.getExchange().get('update_status') &&
            conversation.getExchange().get('update_status') === Exchange.UPDATE_STATUS_NEED_BILLING
        ) {
            return _.first(conversation.getAllExchanges());
        }

        return conversation.getExchange();
    }

    render() {
        const { conversation } = this.props;
        const exchange = this.getExchangeToDisplay(conversation);
        const location = exchange.get('home').get('location');
        if (!exchange.get('update_status') && exchange.get('status') < Exchange.STATUS_FINALIZED) {
            return false;
        }
        const user =
            exchange.get('guest').id == this.props.user.id ? exchange.get('host') : exchange.get('guest');
        const isGuest = exchange.get('guest').id === this.props.user.id;
        return (
            <section className="exchange-infos with-sidebar" ref={this.infosRef}>
                <div className="body">
                    <div className="row">
                        <div className="col-md-7">
                            <header className="header">
                                <h1>
                                    {exchange.get('guest').id == this.props.user.id
                                        ? i18n.t('exchange:you_go_to', {
                                              location: exchange.get('home').location()
                                          })
                                        : i18n.t('messaging:you_receive', {
                                              first_name: user.get('first_name')
                                          })}
                                </h1>
                                <span className="exchange-id">
                                    {i18n.t('exchange:exchange')} : #{exchange.id}
                                </span>
                            </header>
                        </div>
                        <div className="col-md-5 text-right">
                            {!exchange.isFinishedOrCanceled() && (
                                <ButtonAddToCalendar conversation={conversation} user={this.props.user} />
                            )}
                            <button className="btn btn-secondary btn-print hidden-xs" onClick={this.print}>
                                {i18n.t('common:print')}
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-7">
                            <div className="bordered">
                                <table className="table">
                                    <tbody>
                                        <tr>
                                            <th>
                                                {exchange.get('guest').id == this.props.user.id
                                                    ? i18n.t('exchange:your_exchange_in', {
                                                          first_name: user.get('first_name')
                                                      })
                                                    : i18n.t('messaging:you_receive', {
                                                          first_name: user.get('first_name')
                                                      })}
                                            </th>
                                            <td>
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={i18n.t('home:home.view.url', {
                                                        id: exchange.get('home').id
                                                    })}
                                                >
                                                    {i18n.t('home:see_home')}
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>
                                                {i18n.t('common:name')} :{' '}
                                                <em>
                                                    {user.get('first_name')} {user.get('last_name')}
                                                </em>
                                            </th>
                                            <td>
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={i18n.t('user:user.view.url', {
                                                        id: user.id
                                                    })}
                                                >
                                                    {i18n.t('user:see_profile')}
                                                </a>
                                            </td>
                                        </tr>
                                        {user.has('phone') && (
                                            <tr>
                                                <th>
                                                    {i18n.t('user:phone_number')} :{' '}
                                                    <em>{user.get('phone')}</em>
                                                </th>
                                                <td>
                                                    <a href={`tel:${user.get('phone')}`}>
                                                        {i18n.t('common:call')}
                                                    </a>
                                                </td>
                                            </tr>
                                        )}
                                        <tr>
                                            <th>
                                                {i18n.t('user:email')} : <em>{user.get('email')}</em>
                                            </th>
                                            <td>
                                                <a href={`mailto:${user.get('email')}`}>
                                                    {i18n.t('common:send_email')}
                                                </a>
                                            </td>
                                        </tr>
                                        {exchange.get('guest').id == this.props.user.id && (
                                            <tr>
                                                <th>
                                                    {i18n.t('common:address')} :{' '}
                                                    <em>{exchange.get('home').get('address')}</em>
                                                </th>
                                                <td>
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        href={`https://maps.google.com/maps?daddr=${exchange
                                                            .get('home')
                                                            .get('address')}`}
                                                    >
                                                        {i18n.t('exchange:get_direction')}
                                                    </a>
                                                </td>
                                            </tr>
                                        )}
                                        {this.shouldDisplayInitialExchangeDates() && (
                                            <tr>
                                                <th>
                                                    {`${i18n.t(
                                                        'exchange:exchange-info.exchange-initial-dates'
                                                    )} : `}
                                                    <em>
                                                        {i18n.t('common:from_to_date', {
                                                            from: moment(exchange.get('start_on')).format(
                                                                'L'
                                                            ),
                                                            to: moment(exchange.get('end_on')).format('L')
                                                        })}
                                                    </em>
                                                </th>
                                                <td></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <ExchangeInfosButtons conversation={conversation} user={this.props.user} />
                            {!exchange.isStarted() && isGuest && (
                                <TransportationBanner
                                    className="transportation-banner"
                                    onClickCTA={() => {
                                        return new Promise((resolve) => {
                                            const { fetchNavigation } = useGTM(this.props.user);
                                            Analytics.trackGTM(
                                                'button',
                                                fetchNavigation(
                                                    NAVIGATION_ACTION.CLICK,
                                                    NAVIGATION_AREAS.EXCHANGE_DETAILS,
                                                    NAVIGATION_TEXT.COMPARE_TRANSPORT_OPTIONS
                                                ),
                                                resolve
                                            );
                                        });
                                    }}
                                />
                            )}
                        </div>
                        <div className="col-md-5 hidden-xs map-container">
                            {location && location.latitude && location.longitude ? (
                                <MapGoogle
                                    apiKey={process.env.GOOGLE_MAPS_API_KEY}
                                    mapId={process.env.GOOGLE_MAPS_MAP_ID}
                                    defaultCenter={{
                                        lat: location.latitude,
                                        lng: location.longitude
                                    }}
                                    singleMarker={{
                                        position: {
                                            lat: location.latitude,
                                            lng: location.longitude
                                        },
                                        marker: <MapPin variant="default" decoration="home" size={42} />
                                    }}
                                    zoom={15}
                                    onMapLoad={() => {
                                        Analytics.trackGTM('map', {
                                            event_data: {
                                                action: 'map_open',
                                                text: 'map_open',
                                                area: 'exchange_infos'
                                            }
                                        });
                                    }}
                                    lazyLoad
                                    gestureHandling="cooperative"
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className="sidebar">
                    <ExchangeTabs conversation={this.props.conversation} editable={false} />

                    <div className="exchange-infos-buttons">
                        <Button hierarchy="secondary" onClick={this.back} isFullWidth>
                            {i18n.t('messaging:back_to_conversation')}
                        </Button>
                    </div>
                </div>
            </section>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    fetchAvailabilities: bindActionCreators(fetchAvailabilities, dispatch)
});

export default connect(undefined, mapDispatchToProps)(ExchangeInfos);
